import { QueryClient } from '@tanstack/react-query'
import { environment } from '@/config/environment'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 404 errors
        if (error?.status === 404) return false
        
        // Don't retry on 401/403 errors
        if (error?.status === 401 || error?.status === 403) return false
        
        // Retry up to 3 times for other errors
        return failureCount < 3
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
      // Optimistic updates
      networkMode: 'online',
    },
    mutations: {
      retry: false,
      networkMode: 'online',
    },
  },
})

// Prefetch common queries
export const prefetchCommonQueries = async () => {
  // Prefetch user data if authenticated
  const token = localStorage.getItem('access_token')
  if (token) {
    await queryClient.prefetchQuery({
      queryKey: ['user'],
      queryFn: async () => {
        // This would be your actual user fetch function
        const response = await fetch('/api/user', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!response.ok) throw new Error('Failed to fetch user')
        return response.json()
      },
      staleTime: 10 * 60 * 1000, // 10 minutes
    })
  }
}

// Query invalidation utilities
export const invalidateUserData = () => {
  queryClient.invalidateQueries({ queryKey: ['user'] })
}

export const invalidateUserProfile = () => {
  queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
}

export const invalidateSaunaData = () => {
  queryClient.invalidateQueries({ queryKey: ['saunas'] })
}

export const invalidateBlogData = () => {
  queryClient.invalidateQueries({ queryKey: ['blogs'] })
}

// Optimistic update utilities
export const optimisticUpdate = <T>(
  queryKey: string[],
  updater: (oldData: T | undefined) => T
) => {
  queryClient.setQueryData(queryKey, updater)
}

// Background refetch utilities
export const refetchInBackground = (queryKey: string[]) => {
  queryClient.refetchQueries({ queryKey, type: 'active' })
}

// Cache management
export const clearCache = () => {
  queryClient.clear()
}

export const removeQueries = (queryKey: string[]) => {
  queryClient.removeQueries({ queryKey })
}

// Development utilities
if (environment.FEATURES.DEBUG_MODE) {
  // Expose query client to window for debugging
  ;(window as any).__QUERY_CLIENT__ = queryClient
  
  // Log query cache changes
  queryClient.getQueryCache().subscribe((event) => {
    console.log('Query cache event:', event)
  })
} 